import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from './cn';
import Eyebrow from './Eyebrow';

type Props = {
  title?: string;
  /** Petit titre au-dessus du grand titre (mise en page « projets »). */
  eyebrow?: string;
  children: ReactNode[];
  className?: string;
  /**
   * `cards`: 3 cartes visibles, titre et commandes sur la même ligne (actualités).
   * `projects`: 2 grandes cartes visibles, la suivante dépasse jusqu'au bord de l'écran; petit titre + grand titre,
   * commandes alignées à droite en dessous; les flèches avancent de 2 cartes.
   */
  variant?: 'cards' | 'projects';
};

const arrow = 'flex h-12 w-12 items-center justify-center rounded-full border-2 border-line bg-white text-charcoal transition-colors duration-300 hover:border-brand-dark hover:bg-brand-dark hover:text-white disabled:pointer-events-none disabled:text-muted disabled:opacity-60';

export default function Carousel({ title, eyebrow, children, className, variant = 'cards' }: Props) {
  const projects = variant === 'projects';
  const group = projects ? 2 : 1;
  const track = useRef<HTMLUListElement>(null);
  const [index, setIndex] = useState(0);
  const [edges, setEdges] = useState({ start: true, end: false });
  const [fits, setFits] = useState(false);
  const count = children.length;
  const pages = Math.max(1, Math.ceil(count / group));

  const measure = useCallback(() => {
    const el = track.current;
    if (!el) return;
    const first = el.children[0] as HTMLElement | undefined;
    const step = first ? first.offsetWidth + parseFloat(getComputedStyle(el).columnGap || '0') : 1;
    setIndex(Math.min(count - 1, Math.round(el.scrollLeft / step)));
    setEdges({ start: el.scrollLeft <= 2, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2 });
    setFits(el.scrollWidth <= el.clientWidth + 2);
  }, [count]);

  useEffect(() => {
    measure();
    const el = track.current;
    if (!el) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const goToPage = (p: number) => {
    const el = track.current;
    const clamped = Math.max(0, Math.min(pages - 1, p));
    const child = el?.children[Math.min(count - 1, clamped * group)] as HTMLElement | undefined;
    if (el && child) el.scrollTo({ left: child.offsetLeft - (el.children[0] as HTMLElement).offsetLeft, behavior: 'smooth' });
  };

  const page = edges.end && !edges.start ? pages - 1 : Math.min(pages - 1, Math.floor(index / group));
  const canScroll = !fits && pages > 1;

  const controls = canScroll && (
    <div className={cn('flex items-center gap-6', projects && 'justify-end')}>
      <div className="flex items-center gap-2" aria-hidden>
        {Array.from({ length: pages }, (_, n) => (
          <button key={n} tabIndex={-1} onClick={() => goToPage(n)} className={cn('h-2 w-2 rounded-full transition-colors duration-300', n === page ? 'bg-brand-dark' : 'bg-line hover:bg-muted')} />
        ))}
      </div>
      <div className="flex gap-4">
        <button aria-label="Précédent" disabled={edges.start} onClick={() => goToPage(page - 1)} className={arrow}>
          <svg viewBox="0 0 24 12" className="h-3 w-6" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M24 6H1M6 1L1 6l5 5" /></svg>
        </button>
        <button aria-label="Suivant" disabled={edges.end} onClick={() => goToPage(page + 1)} className={arrow}>
          <svg viewBox="0 0 24 12" className="h-3 w-6" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M0 6h23M18 1l5 5-5 5" /></svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className={className}>
      {projects ? (
        <>
          {eyebrow && <Eyebrow className="mb-4">{eyebrow}</Eyebrow>}
          {title && <h2 className="mb-6 font-display text-[2rem] font-bold normal-case leading-[1.1] tracking-normal text-black md:text-[3rem]">{title}</h2>}
          <div className="mb-8 min-h-12 md:mb-10">{controls}</div>
        </>
      ) : (
        <div className="mb-8 flex items-center justify-between gap-6 md:mb-12">
          {title && <h2 className="text-inherit font-display text-[1.75rem] font-bold normal-case leading-tight tracking-normal text-black md:text-[2.25rem]">{title}</h2>}
          {controls}
        </div>
      )}

      <ul
        ref={track}
        onScroll={measure}
        className={cn(
          'story-track flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          // Sur grand écran, la rangée de projets déborde à droite (voir .carousel-bleed); en dessous, elle va d'un bord à l'autre
          projects ? 'carousel-bleed max-md:-mx-6 max-md:px-6' : '-mx-6 px-6 md:mx-0 md:px-0',
        )}
      >
        {children.map((c, n) => (
          <li key={n} className={cn('shrink-0 snap-start', projects ? 'max-sm:w-[82%] sm:max-md:w-[70%] carousel-card-2' : 'w-[82%] sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]')}>{c}</li>
        ))}
      </ul>
    </div>
  );
}
